import { auth } from './firebase';

const r2WorkerUrl = process.env.REACT_APP_R2_WORKER_URL?.replace(/\/+$/, '');
export const MAX_UPLOAD_BYTES = 50 * 1024 * 1024;

const formatFileSize = (bytes) => `${(bytes / (1024 * 1024)).toFixed(1)} MB`;

const requireWorkerUrl = () => {
  if (!r2WorkerUrl) {
    throw new Error('REACT_APP_R2_WORKER_URL is not configured.');
  }
  return r2WorkerUrl;
};

const uploadToR2Worker = async ({ linkId, file, folder, field, publicUpload = false }) => {
  if (!publicUpload && !auth.currentUser) {
    throw new Error('You must be signed in before uploading files.');
  }

  if (file.size > MAX_UPLOAD_BYTES) {
    throw new Error(`${file.name} is ${formatFileSize(file.size)}. Upload files must be 50 MB or smaller.`);
  }

  const token = auth.currentUser ? await auth.currentUser.getIdToken() : '';
  const response = await fetch(`${requireWorkerUrl()}${publicUpload ? '/response-upload' : '/upload'}`, {
    method: 'POST',
    headers: {
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
      'Content-Type': file.type || 'application/octet-stream',
      'x-file-size': String(file.size || 0),
      'x-link-id': linkId,
      'x-file-name': file.name,
      'x-folder': folder,
      ...(field ? { 'x-field': field } : {}),
    },
    body: file,
  });

  if (!response.ok) {
    const responseText = await response.text();
    let payload = {};
    try {
      payload = responseText ? JSON.parse(responseText) : {};
    } catch (error) {
      payload = { error: responseText };
    }
    throw new Error(payload.error || `Unable to upload file to storage. Worker returned ${response.status}.`);
  }

  return response.json();
};

export const storageService = {
  async uploadAssetFile(linkId, file) {
    const upload = await uploadToR2Worker({
      linkId,
      file,
      folder: 'assets',
    });

    return {
      downloadUrl: upload.downloadUrl,
      path: upload.path,
      contentType: upload.contentType,
      name: upload.name,
      size: upload.size,
    };
  },

  async uploadBrandImage(linkId, field, file) {
    const upload = await uploadToR2Worker({
      linkId,
      file,
      folder: 'brand',
      field,
    });
    return upload.downloadUrl;
  },

  async uploadAssetIcon(linkId, assetId, file) {
    const upload = await uploadToR2Worker({
      linkId,
      file,
      folder: 'asset-icons',
      field: assetId,
    });
    return upload.downloadUrl;
  },

  async uploadResponseFile(linkId, fieldId, file) {
    const upload = await uploadToR2Worker({
      linkId,
      file,
      folder: 'response-files',
      field: fieldId,
      publicUpload: true,
    });

    return {
      downloadUrl: upload.downloadUrl,
      path: upload.path,
      contentType: upload.contentType,
      name: upload.name,
      size: upload.size || file.size || 0,
    };
  },
};
